import type {
  PublishingDocument,
  PublishingGeometry,
  PublishingPage,
} from "./document";

/** Pure resolution by stable master/item identities. Native component trees are
 * referenced, never copied. A derived master may replace an inherited item with
 * the same id. Side filtering happens after inheritance, so replacement means
 * replacement of that item, including its side applicability. */
export function resolveMasterDefinition(
  doc: PublishingDocument,
  masterId: string | undefined,
) {
  const masters = new Map(
    (doc.masters ?? []).map((master) => [master.id, master]),
  );
  const chain: NonNullable<PublishingDocument["masters"]> = [];
  const visiting = new Set<string>();
  let current = masterId;
  while (current) {
    if (visiting.has(current))
      throw new Error("Cyclic publishing master inheritance");
    if (chain.length >= 64)
      throw new Error("Publishing master inheritance exceeds 64 levels");
    visiting.add(current);
    const master = masters.get(current);
    if (!master) throw new Error(`Missing publishing master ${current}`);
    chain.unshift(master);
    current = master.basedOn;
  }
  let geometry: PublishingGeometry = { ...doc.defaults };
  type MasterItem = NonNullable<PublishingDocument["masters"]>[number]["items"][number];
  const items = new Map<string, {
    item: MasterItem;
    sourceMasterId: string;
    inherited: boolean;
    replacesParent: boolean;
  }>();
  for (const master of chain) {
    geometry = { ...geometry, ...master.geometry };
    for (const item of master.items) {
      items.set(item.id, {
        item,
        sourceMasterId: master.id,
        inherited: master.id !== masterId,
        replacesParent: items.has(item.id),
      });
    }
  }
  if (items.size > 1000)
    throw new Error("Resolved publishing master exceeds 1000 items");
  return { geometry, items: [...items.values()] };
}

export function resolveMasterAssignment(
  doc: PublishingDocument,
  page: PublishingPage,
) {
  const resolved = resolveMasterDefinition(doc, page.masterId);
  const items = new Map(resolved.items.map(({ item }) => [item.id, item]));
  for (const override of page.masterOverrides ?? []) {
    const item = items.get(override.itemId);
    if (!item)
      throw new Error(`Unknown inherited publishing item ${override.itemId}`);
    if (override.hidden) {
      items.delete(override.itemId);
      continue;
    }
    items.set(item.id, {
      ...item,
      ...(override.componentId !== undefined
        ? { componentId: override.componentId }
        : {}),
      ...(override.printState !== undefined
        ? { printState: override.printState }
        : {}),
      ...(override.position !== undefined
        ? { position: override.position }
        : {}),
    });
  }
  return {
    geometry: { ...resolved.geometry, ...page.geometry },
    items: [...items.values()],
  };
}
